import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { AspectRatioWrapper, ExternalLink, IconSpinner, NamedLink, ResponsiveImage } from '..';
import Icons from '../Icons/Icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { richText } from '../../util/richText';
import { SUBSCRIPTION_TYPES } from '../../util/enums';

import css from './SchoolListingCard.module.css';
import { getUserSubscription } from '../../util/userDataExtraction';
import { capitalizeFirstSpacer, isArrayLength } from '../../util/genericHelpers';
import { divisionOptions } from '../../config/configExtras';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../containers/FavoritesPage/FavoritesPage.duck';
import { userSelector } from '../../ducks/user.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const SchoolListingCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    isLandingListingCard,
    isSearchPage,
    openFavoritesModal,
    setFirstImage,
    hideFavorite,
  } = props;
  const dispatch = useDispatch();
  const config = useConfiguration();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const currentUser = useSelector(userSelector);
  const { favorites = [], removedItemId } = useSelector(favoritesSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUserId = currentUser?.id?.uuid;
  const listingId = listing?.id?.uuid;

  const favoritesList = favorites || [];

  const isOwnListing = currentUserId && listing?.author?.id?.uuid === currentUserId;
  const isAlreadyAddedToFavoriteslist =
    isArrayLength(favoritesList) &&
    favoritesList.some(category => category.ids?.includes(listingId));
  const classes = classNames(rootClassName || css.root, className, css.brandListingCard, {
    [css.landingListingCard]: isLandingListingCard,
  });

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description, publicData } = currentListing.attributes || {};

  const {
    contactTitle,
    pub_user_type_sct,
    city = '',
    state = '',
    conference,
    division,
    customLinks = [],
  } = publicData || {};

  const slug = createSlug(title);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'scaled-medium',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const navigateToListing = () => {
    history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {}));
  };

  const groupPlusMaybe = Boolean(
    getUserSubscription(currentListing?.author) === SUBSCRIPTION_TYPES.GROUP_PLUS
  );

  const selectedDivision = division ? divisionOptions.find(e => e.key === division) : null;

  const handleFavoritesList = async e => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isAlreadyAddedToFavoriteslist) {
        await dispatch(
          deleteItemFromFavorites({
            isAuthenticated,
            id: listingId,
            dispatch,
            favorites,
            config,
            isAuthenticated,
            userId: currentUserId,
          })
        );
      } else {
        openFavoritesModal(listingId);
        setFirstImage(firstImage);
      }
    } catch (error) {
      console.log('Error while updating favorites');
    }
  };

  return (
    <>
      <div className={classes} onClick={navigateToListing}>
        <div className={css.schoolCard}>
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
            {!isOwnListing && !hideFavorite && (
              <span
                className={classNames(css.favorite, isAlreadyAddedToFavoriteslist && css.favorited)}
                onClick={e => {
                  e.stopPropagation();
                  isAuthenticated
                    ? handleFavoritesList(e)
                    : history.push(
                        createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
                      );
                }}
              >
                {removedItemId === listingId ? <IconSpinner /> : <Icons name="star" />}
              </span>
            )}
          </AspectRatioWrapper>

          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.cardTitle}>
                <h2>
                  {richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </h2>
                <span className={css.brandPremier}>
                  <Icons name="verify" />
                  <FormattedMessage id="SchoolListingCard.verified" />{' '}
                  {`${capitalizeFirstSpacer(pub_user_type_sct)} ${
                    groupPlusMaybe ? 'Plus' : 'Standard'
                  }`}
                </span>
              </div>
              {isSearchPage && (
                <>
                  {/* {contactTitle && (
                  <p className={css.contactInfo}>
                    <strong>
                      <FormattedMessage id="SchoolListingCard.contactTitle" />
                    </strong>{' '}
                    {contactTitle}
                  </p>
                )} */}
                </>
              )}

              <p className={css.cardDescription}>{description}</p>
              {(city || state) && (
                <p className={css.contactInfo}>
                  <strong>
                    <FormattedMessage id="SchoolListingCard.location" />
                  </strong>{' '}
                  {city && state ? `${city}, ${state}` : city || state}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className={css.info}>
          <div className={css.mainInfo}>
            {selectedDivision?.value && (
              <p className={css.contactInfo}>
                <strong>
                  <FormattedMessage id="SchoolListingCard.division" />
                </strong>{' '}
                {selectedDivision?.value}
              </p>
            )}
            {conference && (
              <p className={css.contactInfo}>
                <strong>
                  <FormattedMessage id="SchoolListingCard.conference" />
                </strong>{' '}
                {conference}
              </p>
            )}
            {isArrayLength(customLinks) && (
              <div className={classNames(css.contactInfo, css.affiliationBubblesInfo)}>
                <strong>
                  <FormattedMessage id="SchoolListingCard.affiliation" />
                </strong>
                <div className={css.affiliationBubbles}>
                  {customLinks.map((link, index) => (
                    <ExternalLink
                      onClick={e => e.stopPropagation()}
                      key={index}
                      className={css.affiliationBubble}
                      target={'_blank'}
                      href={link?.url}
                    >
                      {link?.name}
                    </ExternalLink>
                  ))}
                </div>
              </div>
            )}
            <NamedLink className={css.findLink} name="LandingPage">
              <FormattedMessage id="SchoolListingCard.explore" />
              <Icons name="rightArrow" />{' '}
            </NamedLink>
          </div>
        </div>
      </div>
    </>
  );
};

SchoolListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

SchoolListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(SchoolListingCardComponent);
